<template>
  <input
    v-bind="inputAttrs"
    :id="id"
    type="checkbox"
    :name="name"
    :checked="isChecked"
    :disabled="disabled"
    :aria-label="ariaLabel"
    :aria-describedby="ariaDescribedby"
    :aria-readonly="readonly || undefined"
    :class="checkboxClasses"
    @click="handleClick"
    @change="handleChange"
  />
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Controlled checkbox state. */
    modelValue?: boolean | undefined
    /** Uncontrolled checked state used when modelValue is omitted. */
    checked?: boolean
    /** Optional id applied to the checkbox input. */
    id?: string | undefined
    /** Optional form field name. */
    name?: string | undefined
    /** Visual size of the checkbox. */
    size?: 'sm' | 'md'
    /** Visual color variant of the checkbox. */
    variant?: 'primary' | 'error'
    /** Corner radius of the checkbox. */
    rounded?: 'sm' | 'md'
    /** Disables interaction with the checkbox. */
    disabled?: boolean
    /** Marks the checkbox as read-only. */
    readonly?: boolean
    /** Accessible name when no visible label is associated with the checkbox. */
    ariaLabel?: string | undefined
    /** Id of the element that provides an accessible description. */
    ariaDescribedby?: string | undefined
  }>(),
  {
    ariaDescribedby: undefined,
    ariaLabel: undefined,
    id: undefined,
    modelValue: undefined,
    name: undefined,
    rounded: 'md',
    size: 'md',
    variant: 'primary'
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  checkedChange: [event: Event]
}>()

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const isChecked = computed(() => {
  if (props.modelValue !== undefined) {
    return props.modelValue
  }

  return props.checked ?? false
})

const sizeClass = computed(() => (props.size === 'sm' ? 'size-5' : 'size-6'))
const roundedClass = computed(() => (props.rounded === 'sm' ? 'rounded-sm' : 'rounded-md'))

const checkboxClasses = computed(() => {
  return [
    'app-checkbox inline-block shrink-0 cursor-pointer appearance-none align-middle',
    'border shadow-control-inset',
    'transition-[background-color,border-color,box-shadow] duration-200 ease-in-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-focus-level-900',
    'disabled:cursor-not-allowed disabled:opacity-20',
    isChecked.value
      ? [
          'app-checkbox-checked',
          props.variant === 'error'
            ? 'border-danger-500 bg-danger-500 shadow-control-checked-inset focus-visible:ring-danger-500/40'
            : 'border-primary bg-primary shadow-control-checked-inset focus-visible:ring-primary/40'
        ]
      : 'border-border-level-300/80 bg-surface-canvas dark:border-border-inverse/20 dark:bg-surface-canvas/[0.03]',
    sizeClass.value,
    roundedClass.value,
    attrs.class
  ]
})

const inputAttrs = computed(() => {
  const { class: _class, ...rest } = attrs
  return rest
})

const handleClick = (event: MouseEvent) => {
  if (props.readonly) {
    event.preventDefault()
  }
}

const handleChange = (event: Event) => {
  if (!(event.target instanceof HTMLInputElement)) {
    return
  }

  const target = event.target

  if (props.readonly) {
    target.checked = isChecked.value
    return
  }

  emit('checkedChange', event)

  if (props.modelValue !== undefined) {
    emit('update:modelValue', target.checked)
  }
}
</script>

<style scoped>
.app-checkbox-checked {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='white'%3E%3Cpath d='M12.207 4.793a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414 0l-2.5-2.5a1 1 0 0 1 1.414-1.414L6.5 9.086l4.293-4.293a1 1 0 0 1 1.414 0z'/%3E%3C/svg%3E");
  background-size: 0.75rem 0.75rem;
  background-position: center;
  background-repeat: no-repeat;
}
</style>
